// @intl project:provider-profile

import { defineMessages, IntlShape } from 'react-intl'

import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { compact, get, isArray, isEmpty, isNil, startCase, trim } from 'lodash-es'

import {
  PROVIDER_CONDITIONS,
  PROVIDER_CREDENTIALS,
  PROVIDER_SENSITIVITIES,
  ProviderKeywordType,
  ProviderSearchContext,
  ProviderTypesPluralEnum,
  REGISTERED_ACSW_CREDENTIAL,
} from './constants'
import {
  DEFAULT_PROGRAM_TAXONOMY,
  PROGRAM_TAXONOMY,
  PROGRAM_TAXONOMY as PROGRAM_TAXONOMY_CONSTANTS,
} from '../../features/common/constants/customerProgram'
import { TREATMENT_RECOMMENDATION_TIER } from '../../features/common/constants/treatmentRecommendationsConstants'
import { Appointment } from '../../models/appointment/Appointment'
import { InPersonLocation, ModalityType, ProviderAvailabilities } from '../../models/calendar/CalendarV2'
import { Episode, EpisodeStates } from '../../models/episodes/Episodes'
import { ICD10_PRIMARY_FIELD, ICD10_SECONDARY_FIELD } from '../../models/icd10/constants'
import { AvailableCodes, ICD10 } from '../../models/icd10/ICD10'
import { Infraction } from '../../models/infraction/Infraction'
import { LTProgramName, Program, ProgramNames } from '../../models/programs/Programs'
import {
  Address,
  BCBA_CREDENTIAL_TITLE,
  COUNSELLOR_DISPLAY_TITLE,
  CredentialParts,
  DistanceToProviderValue,
  DistanceUnitAbbreviation,
  NEURODIVERSITY_SKILLS_COACH_DISPLAY_TITLE,
  PreferredContactInfo,
  ProgramNamesInProviderCapacityToTreatmentNames,
  Provider,
  PROVIDER_TYPES,
  ProviderCalendar,
  ProviderCapacity,
  ProviderEnums,
  ProviderExplainClinicalMatch,
  ProviderInfo,
  ProviderProgramTaxonomy,
  ProviderType,
  TreatmentModeEnums,
  TreatmentNamesToProgramNamesInProviderCapacity,
} from '../../models/providers/Providers'
import { APPOINTMENT_STATUS, MEETING_FORMATS } from '../../models/session/Session'
import { ProviderUser } from '../../models/user/User'
import { filterTimeSlotByOnsiteLocation } from '../appointments/utils'
import { SINGLE_MODALITY_SUPPORTED_MODES, TREATMENT_MODE } from '../common/constants/appointmentModalityConstants'
import { S4C_PROPERTIES } from '../common/constants/mixpanelConstants'
import { TREATMENT_OPTIONS, TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'
import { stableShuffleArrayWithUUID } from '../common/utils/commonUtils'
import { is24HoursInTheFuture } from '../common/utils/dateUtils'
import { startCaseDelimitedString } from '../common/utils/stringUtils'
import { FavoritedProvidersMetadata } from '../favoritedProviders/types'
import { getRemainingDaysUntil7DayExpiration, isProviderAvailableForBooking } from '../favoritedProviders/utils'
import { MEMBER_PREFERENCE_BE_TO_ID_MAPPING } from '../memberPreferences/constants'
import { Programs } from '../programConfigs/programConfigs'
import { isCTPOForProgram } from '../programConfigs/programHelpers'
import { isKnownTreatmentCTPO } from '../programConfigs/treatmentOptionProgramTaxonomyMappings'
import {
  PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG,
  PROVIDER_CONDITIONS_MAPPING_TO_TRANSLATION_MSG,
} from '../searchForCare/messages'
import { convertPrimaryAndTriageTermsToMessages } from '../searchForCare/utils'

/**
 * Creates a Set of valid ICD10 codes for quick lookup
 *
 * @param codes - An object containing an array of all current and valid ICD10 code objects usually from S3 bucket.
 * @returns A Set containing ICD10 codes from S3 bucket in this construct: new Set(["F40.9", "F13.24"]). If `codes.all` is missing/invalid,
 * returns an empty set.
 * @example
 * const availableCodes = { all: [{ code: "F40.9" }, { code: "F13.24" }] };
 * const validCodesSet = getAllAvailableCodesSet(availableCodes);
 * console.log(validCodesSet.has("F40.9")); // true
 * console.log(validCodesSet.has("X99.99")); // false
 */

export const getAllAvailableCodesSet = (codes: AvailableCodes) =>
  new Set((codes?.all ?? []).map((icd10: ICD10) => icd10.code))

/**
 * Parses a comma-separated string of ICD10 codes, filtering out any that are not present in `availableCodes`.
 *
 * @param codesString - A comma-separated list of ICD10 codes (e.g., "F40.9,F13.24").
 * @param availableCodes - An object containing an array of all valid ICD10 code objects usually from S3 bucket.
 * @returns Current and valid ICD10[] corresponding to the codes in `codesString`.
 *          If `codesString` is empty or if `availableCodes.all` is missing/invalid, returns an empty array.
 */
export const parseIcd10Codes = (codesString: string | undefined, availableCodes: AvailableCodes): ICD10[] => {
  if (!codesString || !Array.isArray(availableCodes.all)) return []

  const validCodesSet = getAllAvailableCodesSet(availableCodes)

  return codesString
    .split(',')
    .map((code) => code.trim())
    .filter((code) => validCodesSet.has(code))
    .map((code) => availableCodes.all.find((entry) => entry.code === code))
    .filter(Boolean) as ICD10[]
}

/**
 * Extracts and validates ICD10 codes from the `charge` object for both primary and secondary diagnoses.
 *
 * @param availableCodes - An object containing an array of all valid ICD10 code objects usually from S3 bucket.
 * @param charge - The current health plan charge object that may contain `icd10_primary_diagnosis` and/or `icd10_secondary_diagnosis` strings.
 * @returns An object with the structure:
 *          {
 *            [ICD10_PRIMARY_FIELD]: ICD10[],
 *            [ICD10_SECONDARY_FIELD]: ICD10[],
 *          }
 *          If no valid codes are found, returns empty arrays for each respective field. If `availableCodes` is invalid, returns an empty object.
 */
export const getInitialICD10s = (
  availableCodes: AvailableCodes,
  charge: { data?: { icd10_primary_diagnosis?: string; icd10_secondary_diagnosis?: string } },
) => {
  if (!availableCodes || !Array.isArray(availableCodes.all)) return {}

  const primaryCodes = parseIcd10Codes(charge?.data?.icd10_primary_diagnosis, availableCodes)
  const secondaryCodes = parseIcd10Codes(charge?.data?.icd10_secondary_diagnosis, availableCodes)

  return {
    [ICD10_PRIMARY_FIELD]: primaryCodes,
    [ICD10_SECONDARY_FIELD]: secondaryCodes,
  }
}

/**
 * Finds a list of BCProviders given a list of providers and episodes
 * This will check if the providers in the list are blended care and map
 * episodes with providers to ensure the provider is a valid BCx type
 */
export const getBCProviders = (providers: Provider[], episodes: Episode[]): Provider[] => {
  return providers
    .filter((provider) => isProviderBlendedCareType({ provider: provider.provider }))
    .filter((provider) => !!episodes.find((episode) => episode.provider_id === provider.provider.lyra_id))
}

export const getDAMigratedProviders = (providers: Provider[], episodes: Episode[]): Provider[] => {
  return providers
    .filter((provider) => provider.provider.isDAProviderMigrated)
    .filter((provider) => !!episodes.find((episode) => episode.provider_id === provider.provider.lyra_id))
}

/**
 * With a provider, check that a provider is a blended care type
 * @deprecated, please use `isBlendedCareProvider` going forward
 */
export const isProviderBlendedCareType = ({ provider }: { provider: ProviderInfo }): boolean => {
  return provider && provider.program_taxonomy
    ? isBlendedCareProvider({ programTaxonomy: provider.program_taxonomy })
    : false
}

/**
 * Helper function to help filter a list of providers that match a list of types
 */
export const isProviderAnyTypeOf = ({
  provider,
  providerTypes = [],
}: {
  provider: ProviderInfo
  providerTypes: ProviderType[]
}): boolean => {
  return providerTypes.some((providerType) => {
    return (
      provider &&
      (provider.lyra_type ?? PROVIDER_TYPES[ProviderEnums.PROVIDER].lyraType).toLowerCase() === providerType.lyraType
    )
  })
}

export const isDAProvider = ({ programTaxonomy = [] }: { programTaxonomy?: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some((pt) => pt.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess)
    : false
}

export const isDAProviderOfferingTherapy = ({
  programTaxonomy = [],
}: {
  programTaxonomy: ProviderProgramTaxonomy[]
}) => {
  return programTaxonomy
    ? programTaxonomy.some(
        (pt) =>
          pt.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.therapy &&
          pt.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess,
      )
    : false
}

export const isDAProviderOfferingMeds = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some(
        (pt) =>
          pt.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.medicationManagement &&
          pt.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess,
      )
    : false
}

export const isBlendedCareProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some(
        (taxonomy: ProviderProgramTaxonomy) => taxonomy.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.blendedCare,
      )
    : false
}

export const isBCTFellowshipProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some((pt) => pt?.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.fellowship)
    : false
}

export const isBCTProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  if (!programTaxonomy || !isBlendedCareProvider({ programTaxonomy })) {
    return false
  }
  return programTaxonomy.some((pt) => pt.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.therapy)
}

export const isBCCProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  if (!programTaxonomy || !isBlendedCareProvider({ programTaxonomy })) {
    return false
  }
  return programTaxonomy.some((pt) => pt.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.coaching)
}

export const isBCMProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  if (!programTaxonomy || !isBlendedCareProvider({ programTaxonomy })) {
    return false
  }
  return programTaxonomy.some((pt) => pt.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.medicationManagement)
}

export const isCLEProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some((pt) => pt.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.clinicalLeave)
    : false
}

export const isNeurodiversityProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some((pt) => pt.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.neurodiversitySkills)
    : false
}

export const isPerformanceProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy.some((pt) => isCTPOForProgram(pt, ProgramNames.PerformanceCoaching))
}

export const isDAMedsProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy.some((pt) => isCTPOForProgram(pt, ProgramNames.DIRECT_ACCESS_MEDS))
}

export const isCaregiverTrainingForNeuroProvider = ({
  programTaxonomy = [],
}: {
  programTaxonomy: ProviderProgramTaxonomy[]
}) => {
  return programTaxonomy
    ? programTaxonomy.some((pt) => pt.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.caregiverTrainingForNeuro)
    : false
}

export const isICASCounsellor = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy ? programTaxonomy.some((pt) => pt?.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.icas) : false
}

export const isDBTProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy.some((pt) => pt.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.dbt)
}

export const isLMMProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy.some((pt) => pt.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.medicationManagement)
}

export const isDBTOrLMMProvider = ({ programTaxonomy = [] }: { programTaxonomy: ProviderProgramTaxonomy[] }) => {
  return isDBTProvider({ programTaxonomy }) || isLMMProvider({ programTaxonomy })
}

/**
 * Returns the appropriate Individual Therapy CTPO based on the provider's Partner attribute.
 * Used for direct path booking to determine whether to use LC, ICAS, or DA CTPO.
 *
 * Logic:
 * - IF Partner=BlendedCare THEN return LCT CTPO
 * - ELSE IF Partner=Icas THEN return ICAS CTPO
 * - ELSE return DA CTPO (covers DirectAccess or any customer-specific partner name)
 *
 * WARNING: This function only derives top-level therapy taxonomies. It will only return the main
 * DA, LC, or ICAS CTPOs. There are additional therapy "flavors" (DA Family Therapy, DA Couples
 * Therapy, LC Fellowship Therapy) that have their own CTPOs, and this function does not handle
 * that level of granularity. It only returns the core therapy taxonomies based on the ambiguous
 * individualTherapy treatmentOption and the provider's Partner.
 *
 * This generalization is acceptable for the direct path use case because bookings with the
 * individualTherapy treatment option should always book with the main therapy program. If a user
 * needs one of the lower-level therapy offerings, the direct link will include the explicit CTPO,
 * which will override this behavior.
 */
export const getIndividualTherapyCTPOForProvider = ({
  programTaxonomy,
}: {
  programTaxonomy?: ProviderProgramTaxonomy
}): ProviderProgramTaxonomy => {
  const partner = programTaxonomy?.partner

  const resolvedPartner =
    partner === PROGRAM_TAXONOMY_CONSTANTS.partner.blendedCare || partner === PROGRAM_TAXONOMY_CONSTANTS.partner.icas
      ? partner
      : PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess

  return {
    clientele: PROGRAM_TAXONOMY_CONSTANTS.clientele.individual,
    treatment: PROGRAM_TAXONOMY_CONSTANTS.treatment.therapy,
    partner: resolvedPartner,
    offering: PROGRAM_TAXONOMY_CONSTANTS.offering.default,
  }
}

export const isAppleOnsiteProvider = ({ programTaxonomy = [] }: { programTaxonomy?: ProviderProgramTaxonomy[] }) => {
  return programTaxonomy
    ? programTaxonomy.some(
        (pt) => pt?.treatment === PROGRAM_TAXONOMY_CONSTANTS.treatment.therapy && pt?.partner === 'Apple',
      )
    : false
}

export const isDirectAccessProvider = (provider?: ProviderUser) => {
  return provider
    ? provider.program_taxonomies?.some(
        (pt: ProviderProgramTaxonomy) => pt?.partner === PROGRAM_TAXONOMY.partner.directAccess,
      )
    : false
}

export const hasDirectAccessProvider = ({ providers = [] }: { providers?: Provider[] }) => {
  return (
    providers?.some((p) =>
      p?.provider?.program_taxonomy?.some((pt) => pt?.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess),
    ) ?? false
  )
}

/**
 * Helper to get the episode current session count with a provider
 * @param {*} providerId string
 * @param {*} episodeId string
 * @param {*} providers Providers
 * @returns number | string | undefined
 */
export const getProviderSessionCounts = (
  providerId: string,
  episodeId: string | null,
  providers: Provider[],
): number => {
  const provider = providers.find((provider) => provider.provider.lyra_id === providerId)
  return episodeId && provider && provider.provider.episodeSessionCounts
    ? provider.provider.episodeSessionCounts[episodeId]
    : 0
}

export const getProviderSupportsInPerson = ({ treatmentMode }: { treatmentMode: TreatmentModeEnums }) => {
  const inPersonTreatments = [
    TreatmentModeEnums.ONLY_INPERSON,
    TreatmentModeEnums.BOTH_VIDEO_INPERSON,
    TreatmentModeEnums.INPERSON_PHONE_VIDEO,
    TreatmentModeEnums.BOTH_PHONE_INPERSON,
  ]
  return inPersonTreatments.some((inPersonTreatment) => inPersonTreatment === treatmentMode)
}

// Duplicate in nature to `getProviderSupportsInPerson`.
// TODO: deprecate that aforementioned function since this relies directly on what Arbiter tells us.
export const getProviderSupportsInPersonModality = ({ provider }: { provider: ProviderInfo }) => {
  return provider.sessionFormat?.isInPerson || false
}

export const getProviderSupportsVideo = ({ provider }: { provider: ProviderInfo }) => {
  return provider.sessionFormat?.isVideo || false
}

export const getProviderSupportsLiveMessaging = ({
  provider,
  selectedTreatmentOption,
}: {
  provider?: ProviderInfo
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null
}) => {
  if (!provider) return false

  // Check multiple sources for live messaging support
  const sessionFormatSupport = provider.sessionFormat?.isLiveMessaging ?? false
  const modalitiesSupport = provider.modalities?.some((modality) => modality.includes('stressManagementLM')) ?? false

  const supportsLiveMessaging = sessionFormatSupport || modalitiesSupport

  // If selectedTreatmentOption is passed, check that it's COACHING
  // Otherwise, just return whether provider supports live messaging
  if (!isNil(selectedTreatmentOption)) {
    return supportsLiveMessaging && selectedTreatmentOption === TREATMENT_OPTIONS.COACHING
  }

  return supportsLiveMessaging
}

export const getProviderShouldDisplayLiveMessagingModality = ({
  providerSupportsLiveMessaging,
  shownTierPage,
  outsideOnboarding = false,
}: {
  providerSupportsLiveMessaging: boolean
  shownTierPage?: TREATMENT_RECOMMENDATION_TIER
  outsideOnboarding?: boolean
}) => {
  return providerSupportsLiveMessaging && (shownTierPage === TREATMENT_RECOMMENDATION_TIER.TIER_1 || outsideOnboarding)
}

export const getShowMeetingFormatField = ({
  provider,
  liveMessagingEnabled,
  selectedTreatmentOption,
  outsideOnboarding = false,
  shownTierPage,
  isInProductCalendarModalityEnabledS4C = false,
}: {
  provider?: ProviderInfo
  liveMessagingEnabled: boolean
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null
  outsideOnboarding?: boolean
  shownTierPage?: TREATMENT_RECOMMENDATION_TIER
  isInProductCalendarModalityEnabledS4C?: boolean
}) => {
  if (!provider) {
    return false
  }

  if (isInProductCalendarModalityEnabledS4C) {
    return true
  }
  const supportedVisitTypes = provider?.supportedVisitTypes
  const supportsMoreThanOneModalityFromVisitTypes = !SINGLE_MODALITY_SUPPORTED_MODES.includes(supportedVisitTypes)

  // Check sessionFormat for more accurate modality support detection
  const sessionFormat = provider.sessionFormat
  const supportedModalitiesCount = [
    sessionFormat?.isVideo,
    sessionFormat?.isLiveMessaging,
    sessionFormat?.isInPerson,
  ].filter(Boolean).length

  // Provider supports more than one modality if either:
  // 1. supportedVisitTypes indicates multiple modalities, OR
  // 2. sessionFormat indicates multiple modalities (more accurate), OR
  // 3. customer has live messaging enabled (which means customer + provider both support live messaging)
  //    and provider supports video (creating a video + live messaging combination)
  const hasVideoAndLiveMessaging = liveMessagingEnabled && (provider.video || sessionFormat?.isVideo)
  const supportsMoreThanOneModality =
    supportsMoreThanOneModalityFromVisitTypes || supportedModalitiesCount > 1 || hasVideoAndLiveMessaging

  const isProviderCoach = provider
    ? isProviderAnyTypeOf({ provider, providerTypes: [PROVIDER_TYPES[ProviderEnums.COACH]] })
    : false
  const coachingTreatmentOptions: TREATMENT_OPTIONS_TYPE[] = [
    TREATMENT_OPTIONS.COACHING,
    TREATMENT_OPTIONS.COACHING_FOR_PARENTS,
    TREATMENT_OPTIONS.PERFORMANCE_COACHING,
  ]
  const showMeetingFormatFieldForCoach =
    isProviderCoach &&
    supportsMoreThanOneModality &&
    (!selectedTreatmentOption || coachingTreatmentOptions.includes(selectedTreatmentOption)) &&
    (outsideOnboarding || shownTierPage === TREATMENT_RECOMMENDATION_TIER.TIER_1)

  const isBlendedCareProvider = provider ? isProviderBlendedCareType({ provider }) : false
  const showMeetingFormatField =
    supportsMoreThanOneModality && (!isBlendedCareProvider || showMeetingFormatFieldForCoach)

  return showMeetingFormatField
}

export const getUnavailableFormats = ({
  provider,
  selectedSlotModalityType,
}: {
  provider: ProviderInfo
  selectedSlotModalityType?: ModalityType
}) => {
  const supportedVisitTypes = provider?.supportedVisitTypes
  const supportsMoreThanOneModality = !SINGLE_MODALITY_SUPPORTED_MODES.includes(supportedVisitTypes)

  // If provider only supports one modality, there are no unavailable formats
  if (!supportsMoreThanOneModality) {
    return []
  }

  // If no specific slot modality is provided, return empty array (all formats available)
  if (!selectedSlotModalityType || selectedSlotModalityType === ModalityType.BOTH_IN_PERSON_AND_VIDEO) {
    return []
  }

  const unavailableFormats: MEETING_FORMATS[] = []

  // If the slot only supports video, mark in-person as unavailable
  if (selectedSlotModalityType === ModalityType.VIDEO) {
    unavailableFormats.push(MEETING_FORMATS.IN_PERSON)
  }
  // If the slot only supports in-person, mark video as unavailable
  else if (selectedSlotModalityType === ModalityType.IN_PERSON) {
    unavailableFormats.push(MEETING_FORMATS.VIDEO)
  }

  return unavailableFormats
}

/**
 * Function that returns the matches between provider selected preferences to the selected user member preferences
 */
export const getProviderSupportedPreferenceValues = (
  providerSelectedPreferences: string[],
  userSelectedPreferences: string[],
) => {
  // Map the provider preferences to its ID
  const mappedPreferenceValues = providerSelectedPreferences.map((value) => MEMBER_PREFERENCE_BE_TO_ID_MAPPING[value])
  // Map the user selected preferences to the values received from BE
  return userSelectedPreferences.filter((preferenceID) => mappedPreferenceValues.includes(preferenceID))
}

/**
 * Function to get the meeting format off the provider object if one is not already passed in
 */
export const getMeetingFormat = ({
  meetingFormat,
  provider,
  liveMessagingEnabled,
}: {
  meetingFormat?: MEETING_FORMATS | null
  provider: ProviderInfo
  liveMessagingEnabled: boolean
}) => {
  if (
    meetingFormat &&
    (meetingFormat === MEETING_FORMATS.NONE_SPECIFIED || meetingFormat === MEETING_FORMATS.VIDEO_AND_LIVE_MESSAGING)
  ) {
    if (liveMessagingEnabled) {
      return null
    } else {
      return MEETING_FORMATS.VIDEO
    }
  } else if (meetingFormat) {
    return meetingFormat
  } else if (liveMessagingEnabled) {
    return null
  } else if (provider.supportedVisitTypes === TREATMENT_MODE.ONLY_INPERSON) {
    return MEETING_FORMATS.IN_PERSON
  } else if (provider.supportedVisitTypes === TREATMENT_MODE.ONLY_VIDEO) {
    return MEETING_FORMATS.VIDEO
  } else if (isProviderBlendedCareType({ provider }) && provider.supportedVisitTypes.toLowerCase().includes('video')) {
    return MEETING_FORMATS.VIDEO
  } else {
    return null
  }
}

const getFirstMatchInParentheses = (str: string) => {
  // Returns the first match between parentheses or empty string
  // Ex: "Licensed Marriage and Family Therapist (LMFT)" => "LMFT"
  const matches = str.match(/\(([^)]+)\)/) ?? []

  // matches[0] = entire matched string. e.g. "(LMFT)"
  // matches[1] = first matched *capture-group* (text inside [non-backslash-escaped] parens, EXCLUDING those parens)
  // e.g. matches[1] = "LMFT"
  return (matches[1] ?? '').toUpperCase()
}

/*
 * Returns the explanatory copy that is tied to a specific provider credential.
 */
export const getCredentialExplanation = (
  { formatMessage }: IntlShape,
  provider: ProviderInfo,
  credential: string,
  isPerformanceCoachingProvider = false,
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null,
  selectedTreatmentOptionCTPO?: ProviderProgramTaxonomy | null,
): string => {
  const cred = getFirstMatchInParentheses(credential)

  const CREDENTIAL_EXPLANATIONS = defineMessages({
    APRN: {
      defaultMessage:
        'Masters-level nurse practitioner who can provide evaluation and management of medications for behavioral health conditions.',
      description: "Provider Profile: Contextual copy for provider's credential: APRN",
    },
    LCSW: {
      defaultMessage:
        'Mental health professional with a master’s in social work. Lyra’s LCSWs are trained and licensed to provide therapy using evidence-based approaches.',
      description: "Provider Profile: Contextual copy for provider's credential: LCSW",
    },
    LMFT: {
      defaultMessage:
        'Mental health professional with a master’s in marriage and family therapy. Lyra’s LMFTs are trained and licensed to provide therapy to individuals, couples, and families using evidence-based approaches.',
      description: "Provider Profile: Contextual copy for provider's credential: LMFT",
    },
    MD: {
      defaultMessage:
        'Medical doctor who can provide evaluation and management of medications for behavioral health conditions.',
      description: "Provider Profile: Contextual copy for provider's credential: MD",
    },
    PHD: {
      defaultMessage:
        'Mental health professional with a doctorate in psychology, trained and licensed to provide therapy using a variety of approaches.',
      description: "Provider Profile: Contextual copy for provider's credential: PhD",
    },
    PSYD: {
      defaultMessage:
        'Mental health professional with a doctorate in psychology, trained and licensed to provide therapy using a variety of approaches.',
      description: "Provider Profile: Contextual copy for provider's credential: PsyD",
    },
    'REGISTERED ACSW': {
      defaultMessage:
        'ACSWs are completing their licensure in the Lyra Clinical Fellowship program. Each provider has 900+ hours of supervised clinical experience.',
      description: "Provider Profile: Contextual copy for provider's credential: Registered ACSW",
    },
  })

  if (CREDENTIAL_EXPLANATIONS[cred]) {
    return formatMessage(CREDENTIAL_EXPLANATIONS[cred])
  }

  /*
   * Known credentials not listed above, fall back to generic context by provider type
   * CMHC
   * CPC
   * LCPC
   * LICSW
   * LMHC
   * LPC
   * LPC-MHSP
   * LPCC
   */
  if (isProviderAnyTypeOf({ provider, providerTypes: [PROVIDER_TYPES[ProviderEnums.COACH]] })) {
    if (isPerformanceCoachingProvider) {
      return ''
    }
    // Suppress mental health coaching description for specific search contexts
    // Use search context to determine if this is a specialized coaching search
    const searchContext = resolveProviderSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO)
    if (searchContext === ProviderSearchContext.NSC || searchContext === ProviderSearchContext.CGT) {
      return ''
    }

    // When NO search context exists, NSC/CGT providers should suppress
    // the description to match title suppression behavior
    if (hasNoSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO)) {
      // Multi-CTPO providers with NSC or CGT taxonomy should suppress description
      if (isMultiCTPOProviderWithNSCorCGT(provider)) {
        return ''
      }

      // Single-CTPO NSC/CGT providers should also suppress description
      if (
        isSingleCTPOProviderWithoutSearchContext(
          provider,
          selectedTreatmentOption,
          selectedTreatmentOptionCTPO,
          isNeurodiversityProvider,
        ) ||
        isSingleCTPOProviderWithoutSearchContext(
          provider,
          selectedTreatmentOption,
          selectedTreatmentOptionCTPO,
          isCaregiverTrainingForNeuroProvider,
        )
      ) {
        return ''
      }
    }

    return formatMessage({
      defaultMessage:
        'Lyra’s mental health coaches draw on principles and practices from evidence-based therapies such as cognitive behavioral therapy (CBT), acceptance and commitment therapy (ACT), and dialectical behavior therapy (DBT).',
      description:
        "Provider Profile: Contextual copy for provider's credential: Generic message for mental health coach",
    })
  } else if (
    isProviderAnyTypeOf({
      provider,
      providerTypes: [PROVIDER_TYPES[ProviderEnums.PSYCHIATRIST], PROVIDER_TYPES[ProviderEnums.LYRAPRESCRIBER]],
    })
  ) {
    return formatMessage({
      defaultMessage:
        'Medical professional who can provide evaluation and management of medications for behavioral health conditions.',
      description:
        "Provider Profile: Contextual copy for provider's credential: Generic message for psychiatrist or physician",
    })
  } else {
    return formatMessage({
      defaultMessage:
        'Mental health professional trained and licensed to provide therapy using a variety of approaches. Depending on the provider, may work with adults, children, couples, or families.',
      description:
        "Provider Profile: Contextual copy for provider's credential: Generic message for mental health professional",
    })
  }
}

export const getLocalizedCredentialTitle = ({ formatMessage }: IntlShape, credential: string): string => {
  if (PROVIDER_CREDENTIALS[credential]) {
    return formatMessage(PROVIDER_CREDENTIALS[credential])
  } else {
    return credential
  }
}

export const getTypeConstantForProvider = (provider: ProviderInfo) => {
  return Object.values(PROVIDER_TYPES).find((providerTypeObject) => {
    const lyraType = provider.lyra_type ? provider.lyra_type : PROVIDER_TYPES[ProviderEnums.PROVIDER].lyraType
    return providerTypeObject.lyraType === lyraType.toLowerCase()
  })
}

/**
 * Resolves the provider search context from treatment option or CTPO signals.
 *
 * This is used to disambiguate provider titles and credentials when a provider supports
 * multiple programs (multi-CTPO providers). The search context indicates which specific
 * program the user is searching for, allowing us to show program-specific information.
 *
 * @param selectedTreatmentOption - The treatment option selected via triage/program search
 * @param selectedTreatmentOptionCTPO - The CTPO from direct program booking links
 * @returns The matching ProviderSearchContext enum value, or null if no specific context applies
 */
const resolveProviderSearchContext = (
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null,
  selectedTreatmentOptionCTPO?: ProviderProgramTaxonomy | null,
): ProviderSearchContext | null => {
  // Check selectedTreatmentOption first (from triage/program search)
  if (selectedTreatmentOption === TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING) {
    return ProviderSearchContext.NSC
  }
  if (selectedTreatmentOption === TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO) {
    return ProviderSearchContext.CGT
  }

  // Check selectedTreatmentOptionCTPO (from direct program booking links)
  if (selectedTreatmentOptionCTPO?.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.neurodiversitySkills) {
    return ProviderSearchContext.NSC
  }
  if (selectedTreatmentOptionCTPO?.offering === PROGRAM_TAXONOMY_CONSTANTS.offering.caregiverTrainingForNeuro) {
    return ProviderSearchContext.CGT
  }

  // No matching search context
  return null
}

// Checks if no search context is provided
const hasNoSearchContext = (
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null | undefined,
  selectedTreatmentOptionCTPO: ProviderProgramTaxonomy | null | undefined,
): boolean => {
  return isNil(selectedTreatmentOption) && isNil(selectedTreatmentOptionCTPO)
}

// Checks if a provider is a multi-CTPO provider with NSC or CGT taxonomy.
const isMultiCTPOProviderWithNSCorCGT = (provider: ProviderInfo): boolean => {
  return (
    provider.program_taxonomy !== undefined &&
    provider.program_taxonomy.length > 1 &&
    (isNeurodiversityProvider({ programTaxonomy: provider.program_taxonomy }) ||
      isCaregiverTrainingForNeuroProvider({ programTaxonomy: provider.program_taxonomy }))
  )
}

/**
 * Determines if a single-CTPO provider should use program-specific behavior when no search context is provided.
 * Multi-CTPO providers are excluded from this check because they require explicit search context
 * to disambiguate which program the user intends to engage with.
 * @param provider - The provider to check
 * @param selectedTreatmentOption - The treatment option selected via triage/program search
 * @param selectedTreatmentOptionCTPO - The CTPO from direct program booking links
 * @param taxonomyPredicate - The predicate function to check if provider has specific taxonomy (e.g., isNeurodiversityProvider, isCaregiverTrainingForNeuroProvider)
 * @returns true if this is a single-CTPO provider with no search context, false otherwise
 */
const isSingleCTPOProviderWithoutSearchContext = (
  provider: ProviderInfo,
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null | undefined,
  selectedTreatmentOptionCTPO: ProviderProgramTaxonomy | null | undefined,
  taxonomyPredicate: (args: { programTaxonomy: ProviderProgramTaxonomy[] }) => boolean,
): boolean => {
  return (
    hasNoSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO) &&
    !(provider.program_taxonomy && provider.program_taxonomy.length > 1) &&
    taxonomyPredicate({ programTaxonomy: provider.program_taxonomy ?? [] })
  )
}

export const getProviderTitle = (
  { formatMessage }: IntlShape,
  provider: ProviderInfo,
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null,
  selectedTreatmentOptionCTPO?: ProviderProgramTaxonomy | null,
): string => {
  // Use search context to determine program-specific title
  const searchContext = resolveProviderSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO)

  if (searchContext === ProviderSearchContext.NSC) {
    return formatMessage(NEURODIVERSITY_SKILLS_COACH_DISPLAY_TITLE)
  }
  if (searchContext === ProviderSearchContext.CGT) {
    // Return empty string for CPC fallback (ProviderProfileHeader will show raw credentials)
    return ''
  }

  // When no search context is provided (both params null/undefined):
  // - Multi-CTPO providers with NSC/CGT taxonomy: return empty string for CPC fallback
  // - Single-CTPO NSC/CGT providers: return their specific title/empty string
  if (hasNoSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO)) {
    // Multi-CTPO providers without search context fall back to showing no title
    // (e.g., favorites page, direct provider link)
    if (isMultiCTPOProviderWithNSCorCGT(provider)) {
      return ''
    }

    // Single-CTPO NSC providers always show their title (no search context needed)
    if (
      isSingleCTPOProviderWithoutSearchContext(
        provider,
        selectedTreatmentOption,
        selectedTreatmentOptionCTPO,
        isNeurodiversityProvider,
      )
    ) {
      return formatMessage(NEURODIVERSITY_SKILLS_COACH_DISPLAY_TITLE)
    }

    // Single-CTPO CGT providers have no title (returns empty string)
    // This is intentional - ProviderProfileHeader will exclude empty strings from subtitle assembly
    if (
      isSingleCTPOProviderWithoutSearchContext(
        provider,
        selectedTreatmentOption,
        selectedTreatmentOptionCTPO,
        isCaregiverTrainingForNeuroProvider,
      )
    ) {
      return ''
    }
  }

  // Otherwise, use existing lyra_type-derived logic
  const providerType = getTypeConstantForProvider(provider)
  // For OUS providers, we will show "Counsellor" iff provider location country is not US
  // This will only be applicable to OUS prioviders who are therapists and should not apply to coaches
  if (
    providerType?.TITLE === PROVIDER_TYPES[ProviderEnums.THERAPIST].TITLE &&
    provider.provider_location_country &&
    provider.provider_location_country !== 'United States'
  ) {
    return formatMessage(COUNSELLOR_DISPLAY_TITLE)
  }
  return formatMessage(providerType?.displayTitle ?? PROVIDER_TYPES.PROVIDER.displayTitle)
}

export const shouldRenderTitleAndEducation = (provider: ProviderInfo): boolean => {
  return provider.credentials ? !provider.credentials.includes(REGISTERED_ACSW_CREDENTIAL) : false
}

/**
 * Returns the credentials to display in the provider profile header subtitle.
 *
 * For multi-CTPO providers, the search context determines which credential to show.
 * Some programs may override the provider's default credentials with program-specific ones.
 * When no search context is available, returns the raw credentials from the backend.
 */
export const getProviderHeaderCredentials = (
  intl: IntlShape,
  provider: ProviderInfo,
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null,
  selectedTreatmentOptionCTPO?: ProviderProgramTaxonomy | null,
): string[] => {
  // Use search context to determine program-specific credential override
  const searchContext = resolveProviderSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO)

  if (searchContext === ProviderSearchContext.CGT) {
    return [intl.formatMessage(BCBA_CREDENTIAL_TITLE)]
  }

  // When no search context is provided, single-CTPO CGT providers return BCBA
  // Multi-CTPO providers must provide search context to get BCBA override
  if (
    isSingleCTPOProviderWithoutSearchContext(
      provider,
      selectedTreatmentOption,
      selectedTreatmentOptionCTPO,
      isCaregiverTrainingForNeuroProvider,
    )
  ) {
    return [intl.formatMessage(BCBA_CREDENTIAL_TITLE)]
  }

  // All other cases return raw credentials
  return provider.credentials ?? []
}

export const getProviderFullCredentials = (
  intl: IntlShape,
  provider: ProviderInfo,
  isPerformanceCoaching = false,
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null,
  selectedTreatmentOptionCTPO?: ProviderProgramTaxonomy | null,
): CredentialParts[] => {
  // Use search context to determine program-specific credential override
  const searchContext = resolveProviderSearchContext(selectedTreatmentOption, selectedTreatmentOptionCTPO)

  if (searchContext === ProviderSearchContext.CGT) {
    return [
      {
        credential: intl.formatMessage(BCBA_CREDENTIAL_TITLE),
        education: shouldRenderTitleAndEducation(provider) ? (provider.education[0] ?? {}).school : undefined,
        context: '',
      },
    ]
  }

  // When no search context is provided, single-CTPO CGT providers return BCBA
  // Multi-CTPO providers must provide search context to get BCBA override
  if (
    isSingleCTPOProviderWithoutSearchContext(
      provider,
      selectedTreatmentOption,
      selectedTreatmentOptionCTPO,
      isCaregiverTrainingForNeuroProvider,
    )
  ) {
    return [
      {
        credential: intl.formatMessage(BCBA_CREDENTIAL_TITLE),
        education: shouldRenderTitleAndEducation(provider) ? (provider.education[0] ?? {}).school : undefined,
        context: '',
      },
    ]
  }

  // Otherwise, return mapped credentials with their explanations
  return provider.credentials
    ? provider.credentials.map((credentialTitle, idx) => ({
        credential: getLocalizedCredentialTitle(intl, credentialTitle),
        education: shouldRenderTitleAndEducation(provider) ? (provider.education[idx] ?? {}).school : undefined,
        context: getCredentialExplanation(
          intl,
          provider,
          credentialTitle,
          isPerformanceCoaching,
          selectedTreatmentOption,
          selectedTreatmentOptionCTPO,
        ),
      }))
    : []
}

export const formatAddressForDisplay = (address?: Address | InPersonLocation) => {
  if (address === undefined) {
    return ''
  }

  let addressLine1 = ''
  let addressLine2 = ''

  addressLine1 = `${address.street1}`.concat(trim(address.street2) ? `, ${address.street2}` : '')
  addressLine2 = `${address.city}, ${address.state} ${address.zipcode}`
  return `${addressLine1}, ${addressLine2}`
}

export const formatMultipleAddressesForDisplay = (addresses?: Address[]) => {
  if (!addresses) {
    return []
  }

  return addresses.map((address) => ({
    id: address.id,
    formattedAddress: formatAddressForDisplay(address),
  }))
}

export const getDoAllProvidersSpeakCareLanguage = ({
  providers,
  careLanguage,
}: {
  providers: ProviderInfo[]
  careLanguage: string
}) => {
  return providers.every((provider: ProviderInfo) => {
    const speaksCareLanguage = provider.languages_bcp47?.find((language) => language?.tag === careLanguage)
    return speaksCareLanguage
  })
}

export const getProviderTypeDisplay = ({
  selectedTreatmentOption,
}: {
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null
}) => {
  switch (selectedTreatmentOption) {
    case TREATMENT_OPTIONS.COACHING:
    case TREATMENT_OPTIONS.COACHING_FOR_PARENTS:
    case TREATMENT_OPTIONS.PERFORMANCE_COACHING:
    case TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING:
      return ProviderTypesPluralEnum.COACHES
    case TREATMENT_OPTIONS.INDIVIDUAL_THERAPY:
    case TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD:
    case TREATMENT_OPTIONS.COUPLES_THERAPY:
    case TREATMENT_OPTIONS.FAMILY_THERAPY:
    case TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD:
    case TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE:
    case TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER:
      return ProviderTypesPluralEnum.THERAPISTS
    case TREATMENT_OPTIONS.BLENDED_CARE_MEDS:
    case TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS:
    case TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD:
    case TREATMENT_OPTIONS.MEDICATION_CONSULTATION:
    case TREATMENT_OPTIONS.CONTROLLED_MEDICATION:
      return ProviderTypesPluralEnum.PRESCRIBERS
    default:
      return ProviderTypesPluralEnum.THERAPISTS
  }
}

export const getTreatmentOptionBasedOnProviderCapacity = ({
  selectedTreatmentOption,
  provider,
}: {
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE
  provider: ProviderInfo
}) => {
  const providerCapacity = provider.providerCapacity
  const providerCapacityKeys = Object.keys(providerCapacity || {})
  const providerHasSingleCapacity = providerCapacityKeys?.length === 1
  return selectedTreatmentOption
    ? selectedTreatmentOption
    : providerHasSingleCapacity
    ? (providerCapacityKeys[0] as TREATMENT_OPTIONS_TYPE)
    : undefined
}

export const getProgramFromSelectedTreatmentOptionAndProviderCapacity = (
  selectedTreatmentOption?: string,
  providerCapacity?: { [key: string]: number },
) => {
  if (isEmpty(providerCapacity)) {
    return null
  }
  const capacities = Object.keys(providerCapacity).map((programName) => [programName, providerCapacity[programName]])
  for (const capacity of capacities) {
    const capacityName = capacity[0] as string
    const program = Programs.getProgramConfigByName(capacityName)
    if (
      selectedTreatmentOption === capacityName ||
      program?.customerPropertyId === selectedTreatmentOption ||
      program?.treatmentOptionProperties?.associatedTriageTreatmentOptions.includes(
        selectedTreatmentOption as TREATMENT_OPTIONS_TYPE,
      )
    ) {
      return program
    }
  }
  return null
}

export const getConflictingEpisodeForProvider = ({
  currentEpisodes,
  selectedTreatmentOption,
  provider,
}: {
  currentEpisodes: Episode[]
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE
  provider: ProviderInfo
}) => {
  return currentEpisodes.find((episode: Episode) => {
    const programFromTreatmentOptionAndProviderCapacity = getProgramFromSelectedTreatmentOptionAndProviderCapacity(
      selectedTreatmentOption,
      provider.providerCapacity,
    )
    if (
      programFromTreatmentOptionAndProviderCapacity &&
      episode.program_id !== programFromTreatmentOptionAndProviderCapacity?.blendedCareProgramId &&
      episode.provider_id !== provider.lyra_id
    ) {
      return false
    } else {
      if (
        episode?.program_name === ProgramNames.TeensTherapy ||
        episode?.program_name === ProgramNames.MedicationManagement
      ) {
        const providerTypes: string[] | undefined = Programs.getProgramConfigByName(
          episode.program_name,
        )?.associatedProviderTypes
        const matchingProgram = providerTypes?.includes(provider.lyra_type.toLowerCase()) ?? false
        return matchingProgram
      } else if (episode?.provider_id === provider.lyra_id) {
        return episode
      } else {
        return false
      }
    }
  })
}

export const getExistingSingleSessionCoachingEpisode = ({ currentEpisodes }: { currentEpisodes: Episode[] }) => {
  return currentEpisodes.find((episode: Episode) => episode.program_name === 'SingleSessionCoaching')
}

export enum ContactType {
  EMAIL = 'email',
  PHONE = 'phone',
  BOTH = 'both',
}

// Determines if preferred contact type for a provider is email, phone or both
export const getProviderPreferredContactType = (email?: string, phone?: string) => {
  if (email && phone) {
    return ContactType.BOTH
  }
  if (email) {
    return ContactType.EMAIL
  } else {
    return ContactType.PHONE
  }
}

/**
 * Return the appropriate contact information for a given provider, based on their preferred contact methods.
 * We have no guarantee on how consistent the provider data will be, so the logic is somewhat complex.
 * @param ProviderInfo
 * @returns contact information
 */
export const getPreferredContactInfo = (provider?: ProviderInfo): PreferredContactInfo => {
  const PHONE_CONTACT = 'phone'
  const EMAIL_CONTACT = 'email'

  if (!provider) {
    return { [PHONE_CONTACT]: undefined, [EMAIL_CONTACT]: undefined }
  }

  /* Compile all possible contact information for a provider.
   * Some providers have contact information for an intake representative - this should be prioritized over provider
   * contact data even when not consistently inputted.
   */
  const allContactInformation = {
    [PHONE_CONTACT]: provider[`intake_representative_${PHONE_CONTACT}`] ?? provider[PHONE_CONTACT] ?? '',
    [EMAIL_CONTACT]: provider[`intake_representative_${EMAIL_CONTACT}`] ?? provider[EMAIL_CONTACT] ?? '',
  }

  /* Determine what the correct contact methods for a given provider are.
   * Any explicit intake representative contact methods supersede a provider's contact methods.
   */
  const intakeRepresentativeContactMethods = provider.intake_representative_contact ?? []
  const providerDirectContactMethods = provider.contact_methods ?? []
  const contactMethods = (
    intakeRepresentativeContactMethods.length === 0 ? providerDirectContactMethods : intakeRepresentativeContactMethods
  )
    ?.map((method) => {
      // Standardize in case of multiple contact methods in one string or mixed casing
      // The split results in an array of arrays, so we have to flatten it afterwards via reduce.
      return method?.toLowerCase()?.split(/\b/)
    })
    .reduce((accumulator, current) => {
      return accumulator.concat(current)
    }, [])

  /* There is no data standardization for a provider's contact methods, e.g. we can expect anything from "phone" to
   * "email" to ["phone", "email"] to "both" to "either / no preference" to no data at all.
   * Thus, display all possible contact methods if neither phone nor email are specified.
   */
  if (!contactMethods.includes(PHONE_CONTACT) && !contactMethods.includes(EMAIL_CONTACT)) {
    return allContactInformation
  } else {
    /* This means that at least one of (phone or email) is specified, so iterate through the listed contact methods
     * and only add the contact information that corresponds to the specified methods.
     */
    const contactInformation = {}
    contactMethods.forEach((method) => {
      if (Object.keys(allContactInformation).includes(method)) {
        contactInformation[method] = allContactInformation[method]
      }
    })
    return contactInformation
  }
}

/** Function that returns whether a program has the ability to reschedule an appointment based on the program name. */
const canCustomerProgramRescheduleAppointment = ({ programName }: { programName: string }): boolean => {
  return Programs.getProgramConfigByName(programName)?.canRescheduleAppointment ?? false
}

/** Function that returns whether or not a valid program name exists and if the provider capacity is
 *  within a certain range.
 */
const doesProviderCapacityFitInGivenRange = ({
  providerCapacity,
  minimumCapacity,
  maximumCapacity,
  selectedTreatmentOption,
}: {
  providerCapacity: { [treatmentOption in string]: number }
  minimumCapacity: number
  maximumCapacity: number
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}) => {
  const programName = TreatmentNamesToProgramNamesInProviderCapacity[selectedTreatmentOption]
  return (
    programName &&
    providerCapacity?.[programName] >= minimumCapacity &&
    providerCapacity?.[programName] <= maximumCapacity
  )
}

/** Function that determines whether a provider is BCT and has a capacity that fits within the given range */
const doesBCTProviderCapacityFitInGivenRange = ({
  programTaxonomy,
  providerCapacity,
  minimumCapacity,
  maximumCapacity,
  selectedTreatmentOption,
}: {
  programTaxonomy: ProviderProgramTaxonomy[]
  providerCapacity: { [treatmentOption in string]: number }
  minimumCapacity: number
  maximumCapacity: number
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}) => {
  return (
    isBCTProvider({ programTaxonomy }) &&
    doesProviderCapacityFitInGivenRange({
      providerCapacity: providerCapacity,
      minimumCapacity,
      maximumCapacity,
      selectedTreatmentOption,
    })
  )
}

/** Function that determines whether a provider is BCM and has a capacity that fits within the given range */
const doesBCMProviderCapacityFitInGivenRange = ({
  programTaxonomy,
  providerCapacity,
  minimumCapacity,
  maximumCapacity,
  selectedTreatmentOption,
}: {
  programTaxonomy: ProviderProgramTaxonomy[]
  providerCapacity: { [treatmentOption in string]: number }
  minimumCapacity: number
  maximumCapacity: number
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}) => {
  return (
    isBCMProvider({ programTaxonomy }) &&
    doesProviderCapacityFitInGivenRange({
      providerCapacity: providerCapacity,
      minimumCapacity,
      maximumCapacity,
      selectedTreatmentOption,
    })
  )
}

/** Function that determines whether a provider is BCC and has a capacity that fits within the given range */
const doesBCCProviderCapacityFitInGivenRange = ({
  programTaxonomy,
  providerCapacity,
  minimumCapacity,
  maximumCapacity,
  selectedTreatmentOption,
}: {
  programTaxonomy: ProviderProgramTaxonomy[]
  providerCapacity: { [treatmentOption in string]: number }
  minimumCapacity: number
  maximumCapacity: number
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}) => {
  return (
    isBCCProvider({ programTaxonomy }) &&
    doesProviderCapacityFitInGivenRange({
      providerCapacity: providerCapacity,
      minimumCapacity,
      maximumCapacity,
      selectedTreatmentOption,
    })
  )
}

/**
 * Function to decide whether to render the SaveYourSpot section in the provider profile
 *
 * As of PROSPECT-3495, do not show SaveYourSpot section if the provider:
 * has no provider capacity saved
 * is not any of BCC/BCT/BCM
 * is BCM but has 3 or more spots left
 */
export const shouldRenderSaveYourSpotSection = ({
  programTaxonomy,
  providerCapacity,
  selectedTreatmentOption,
}: {
  programTaxonomy?: ProviderProgramTaxonomy[]
  providerCapacity?: { [treatmentOption in string]: number }
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}): boolean => {
  if (!programTaxonomy || !providerCapacity) {
    return false
  }

  return (
    doesBCMProviderCapacityFitInGivenRange({
      programTaxonomy,
      providerCapacity,
      minimumCapacity: 1,
      maximumCapacity: 2,
      selectedTreatmentOption,
    }) ||
    isBCCProvider({ programTaxonomy }) ||
    isBCTProvider({ programTaxonomy })
  )
}

/**
 * Function that determines whether or not to show that the current provider has limited
 * availability under the SaveYourSpot section in the provider profile.
 *
 * As of PROSPECT-3114, when a BCM/BCC/BCT provider has 1-2 spots left, show text indicating
 * the provider is frequently booked
 */
export const shouldShowPopularProviderText = ({
  programTaxonomy,
  providerCapacity,
  selectedTreatmentOption,
}: {
  programTaxonomy?: ProviderProgramTaxonomy[]
  providerCapacity?: { [treatmentOption in string]: number }
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}) => {
  if (!programTaxonomy || !providerCapacity) {
    return false
  }
  return (
    doesBCTProviderCapacityFitInGivenRange({
      programTaxonomy,
      providerCapacity,
      minimumCapacity: 1,
      maximumCapacity: 2,
      selectedTreatmentOption,
    }) ||
    doesBCCProviderCapacityFitInGivenRange({
      programTaxonomy,
      providerCapacity,
      minimumCapacity: 1,
      maximumCapacity: 2,
      selectedTreatmentOption,
    }) ||
    doesBCMProviderCapacityFitInGivenRange({
      programTaxonomy,
      providerCapacity,
      minimumCapacity: 1,
      maximumCapacity: 2,
      selectedTreatmentOption,
    })
  )
}

/**
 * Function that determines whether or not to show that the user has the opportunity to
 * adjust their appointments under the SaveYourSpot section in the provider profile
 * based on the program type
 */
export const shouldShowFlexibleReschedulingText = (selectedTreatmentOption: TREATMENT_OPTIONS_TYPE): boolean => {
  const programName = TreatmentNamesToProgramNamesInProviderCapacity[selectedTreatmentOption]
  return programName ? canCustomerProgramRescheduleAppointment({ programName }) : false
}

/**
 * Function that determines whether or not to show a prompting copy around a
 * given provider’s calendar filling up fast
 *
 * As of PROSPECT-3495, only show this text for BCT providers with 3+ availability
 */
export const shouldShowFillsUpFastText = ({
  programTaxonomy,
  providerCapacity,
  selectedTreatmentOption,
}: {
  programTaxonomy?: ProviderProgramTaxonomy[]
  providerCapacity?: { [treatmentOption in string]: number }
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
}) => {
  if (!programTaxonomy || !providerCapacity) {
    return false
  }
  return doesBCTProviderCapacityFitInGivenRange({
    programTaxonomy,
    providerCapacity,
    minimumCapacity: 3,
    maximumCapacity: Number.POSITIVE_INFINITY,
    selectedTreatmentOption,
  })
}

/**
 * Function that returns the value of the treatment option given the provider's program taxonomy.
 * Intended to be used in conjunction with shouldRenderSaveYourSpotSection.
 */
export const getInferredLCXTreatmentOption = ({ programTaxonomy }: { programTaxonomy?: ProviderProgramTaxonomy[] }) => {
  if (isEmpty(programTaxonomy) || !programTaxonomy) {
    return null
  }
  if (isBCTProvider({ programTaxonomy })) {
    return TREATMENT_OPTIONS.INDIVIDUAL_THERAPY
  }

  if (isBCCProvider({ programTaxonomy })) {
    return TREATMENT_OPTIONS.COACHING
  }

  if (isBCMProvider({ programTaxonomy })) {
    return TREATMENT_OPTIONS.BLENDED_CARE_MEDS
  }
  return null
}

// Find a Provider that matches a given Provider ID and return a subset of their information.
export const getProviderDetailsForActivityIntroduction = (providers: Provider[], providerId: string) => {
  const selectedAssignmentProvider =
    providers.length > 0 && providers.find(({ provider }) => provider.lyra_id === providerId)
  return selectedAssignmentProvider
    ? {
        imageUrl: selectedAssignmentProvider.provider.lyraProviderImage.imageUrl,
        displayName: selectedAssignmentProvider.provider.display_name,
        firstName: selectedAssignmentProvider.provider.first_name,
        lyraType: selectedAssignmentProvider.provider.lyra_type,
      }
    : {}
}

// Find a Provider that matches a given Provider ID and return their First and Last name concatenated or an
// empty string
export const getProviderFirstAndLastName = (providers: Provider[], providerId: string | undefined) => {
  const selectedAssignmentProvider =
    providers.length > 0 && providerId && providers.find(({ provider }) => provider.lyra_id === providerId)

  return selectedAssignmentProvider
    ? `${selectedAssignmentProvider.provider.first_name} ${selectedAssignmentProvider.provider.last_name}`
    : ''
}

export const getLocalizedSpecializationsList = ({
  intl: { formatMessage, formatList },
  conditions = [],
  sensitivities = [],
}: {
  intl: IntlShape
  conditions?: string[]
  sensitivities?: string[]
}): string[] => {
  // Falls back to the plain text if the value is not declared in PROVIDER_CONDITIONS
  const formattedSpecializations = conditions.map((condition) => {
    if (PROVIDER_CONDITIONS[condition]) {
      return formatMessage(PROVIDER_CONDITIONS[condition])
    } else {
      const message = convertPrimaryAndTriageTermsToMessages(condition)
      return message ? formatMessage(message) : condition
    }
  })

  if (sensitivities.length > 0) {
    const sensitivitiesList = formatList(
      sensitivities.map((sensitivity) =>
        PROVIDER_SENSITIVITIES[sensitivity] ? formatMessage(PROVIDER_SENSITIVITIES[sensitivity]) : sensitivity,
      ),
      { type: 'conjunction' },
    )
    formattedSpecializations.push(
      formatMessage(
        {
          defaultMessage: 'Experience with {sensitivitiesList} clients',
          description:
            'Provider Profile: Shown in a list of the provider\'s specializations. {sensitivitiesList} contains a list of sensitivities, e.g. "Experience with gay, lesbian, bisexual clients"',
        },
        {
          sensitivitiesList,
        },
      ),
    )
  }

  return formattedSpecializations.sort((a, b) => a.localeCompare(b))
}

export const getShouldDisplayBCTProviderBenefits = ({
  isCustomerDisplayingBCTBenefits = true,
  provider,
}: {
  isCustomerDisplayingBCTBenefits: boolean
  provider?: ProviderInfo
}) => {
  if (!provider || isEmpty(provider)) {
    return false
  }
  return (
    isCustomerDisplayingBCTBenefits &&
    isProviderAnyTypeOf({
      provider,
      providerTypes: [PROVIDER_TYPES[ProviderEnums.LYRATHERAPIST], PROVIDER_TYPES[ProviderEnums.LYRAFELLOW]],
    })
  )
}

export const hasInProgressBCxEpisode = ({ episodes, providers }: { episodes: Episode[]; providers: Provider[] }) => {
  const currentEpisodes = episodes.filter((episode) => episode.state === EpisodeStates.IN_PROGRESS)
  return currentEpisodes.some((currentEpisode) => {
    const episodeProvider = providers?.find((provider) => provider.provider.lyra_id === currentEpisode.provider_id)
    return episodeProvider?.provider ? isProviderBlendedCareType({ provider: episodeProvider.provider }) : false
  })
}

export const hasOnlyDAEngageInfractions = ({
  episodes,
  infractions,
  providers,
  programs,
}: {
  episodes: Episode[]
  infractions: Infraction[]
  providers: Provider[]
  programs: Program[]
}) => {
  const appointments = providers?.map((provider) => provider.appointments).flat()

  if (isNil(infractions) || infractions.length === 0) {
    return false
  }

  return infractions?.every((infraction) => {
    const infractionAppointment = appointments.find((appointment) => appointment.id === infraction.appointmentId)
    if (!infractionAppointment) {
      return false
    }

    const infractionEpisode = episodes.find((episode) => episode.id === infractionAppointment.episodeId)
    if (!infractionEpisode) {
      return false
    }

    const matchingProgram = programs.find((program) => program.id === infractionEpisode.program_id)
    if (!matchingProgram) {
      return false
    }

    const programTaxonomies = matchingProgram.program_taxonomy ?? []
    return (
      programTaxonomies.length > 0 &&
      programTaxonomies.every((pt) => pt.partner === PROGRAM_TAXONOMY_CONSTANTS.partner.directAccess)
    )
  })
}

export const canSeeDirectBookingLink = ({
  provider,
  episodes,
  appointments,
}: {
  provider?: ProviderInfo
  episodes?: Episode[]
  appointments?: Appointment[]
}) => {
  if (!provider) {
    return false
  }

  if (
    !isProviderAnyTypeOf({
      provider,
      providerTypes: [
        PROVIDER_TYPES[ProviderEnums.LYRACLINICALLEAVEEVALUATOR],
        PROVIDER_TYPES[ProviderEnums.LYRAPRESCRIBER],
      ],
    })
  ) {
    return true
  }

  const activeEpisode = episodes?.find((episode) => episode.state === EpisodeStates.IN_PROGRESS)
  if (!activeEpisode) {
    return true
  }

  return !appointments?.some((appointment) => appointment.appointmentStatus !== APPOINTMENT_STATUS.CANCELED)
}

export const getActiveEpisode = ({ episodes, providerId }: { episodes: Episode[]; providerId?: string }) =>
  episodes.find((episode) => episode.provider_id === providerId && episode.state === EpisodeStates.IN_PROGRESS)

export const hasActiveEpisode = ({ episodes, providerId }: { episodes: Episode[]; providerId?: string }) =>
  episodes
    .filter((episode) => episode.provider_id === providerId)
    .some((episode) => episode.state === EpisodeStates.IN_PROGRESS)

export const getProviderAndAppointmentForRebook = ({
  providers,
  episodes,
  input,
}: {
  providers: Provider[]
  episodes: Episode[]
  input?:
    | {
        providerId: string
      }
    | {
        cancelAppointment: Appointment
      }
}) => {
  if (!input) {
    return { provider: null, appointment: null, appointments: [], nonCancelledAppointments: [] }
  }

  const providerId = 'cancelAppointment' in input ? input.cancelAppointment.provider?.lyra_id : input.providerId
  if (!providerId) {
    return { provider: null, appointment: null, appointments: [], nonCancelledAppointments: [] }
  }
  const provider = providers.find((provider) => provider.provider.lyra_id === providerId) ?? null
  const activeEpisode = getActiveEpisode({ episodes, providerId })
  if (!activeEpisode || !provider) {
    return { provider: provider?.provider ?? null, appointment: null, appointments: [], nonCancelledAppointments: [] }
  }
  const activeEpisodeAppointments = provider.appointments.filter(
    (appointment) => appointment.episodeId === activeEpisode?.id,
  )
  const appointment =
    'cancelAppointment' in input
      ? input.cancelAppointment
      : activeEpisodeAppointments.length > 0
      ? activeEpisodeAppointments[activeEpisodeAppointments.length - 1]
      : null

  const nonCancelledAppointments = provider.appointments.filter(
    (appointment) => appointment.appointmentStatus !== APPOINTMENT_STATUS.CANCELED,
  )
  return { provider: provider.provider, appointment, appointments: provider.appointments, nonCancelledAppointments }
}

export const getProviderBookingButtonText = ({
  intl,
  availabilityDate,
  providerTimeZone,
  shouldDisplayMatchedAvailabilities = false,
}: {
  intl: IntlShape
  /** ISO string representing datetime */
  availabilityDate: string
  providerTimeZone: string
  shouldDisplayMatchedAvailabilities?: boolean
}) => {
  const { formatMessage, formatDate } = intl
  // Create date object from iso string in user computer local time
  // Note - despite the method name, this is local time in the user device, NOT local time in the user's profile settings.
  // Arbiter sends matching schedule slots in the provider's timezone, so we can utilize the formatDate function to convert
  // the time into the client's timezone
  const appointmentDateInLocalTime = shouldDisplayMatchedAvailabilities
    ? availabilityDate
    : zonedTimeToUtc(availabilityDate, providerTimeZone)

  // Intl.formatDate retrieves date (Month Day Year) user profile settings specified local time. E.g. 05 1 2024
  const appointmentDateInUserTimeZone = formatDate(appointmentDateInLocalTime, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
  const currentDateInUserTimeZone = formatDate(Date.now(), {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
  const tomorrowDateInUserTimeZone = formatDate(Date.now() + 86400000, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })

  // Note that the below evaluates if the date is today or tomorrow in the time zone specified by the user in their profile settings.
  // What is considered today and tomorrow in user time zone may vary from the time zone (UTC) that was returned in provider availability
  // E.g. 05 2 2024 ?== 05 2 2099
  const isDateToday = appointmentDateInUserTimeZone === currentDateInUserTimeZone
  const isDateTomorrow = appointmentDateInUserTimeZone === tomorrowDateInUserTimeZone

  if (isDateToday || isDateTomorrow) {
    return formatMessage(
      {
        defaultMessage: '{todayOrTomorrow, select, today {Today} tomorrow {Tomorrow} other {today}}, {displayedDate}',
        description:
          'Text for button that redirects members straight to the appointment booking page and the appointment next available appointment is available today.',
      },
      {
        // e.g.: 7:00 PM
        displayedDate: formatDate(appointmentDateInLocalTime, {
          hour: 'numeric',
          minute: 'numeric',
        }),
        todayOrTomorrow: isDateToday ? 'today' : 'tomorrow',
      },
    )
  }
  // e.g.: Mon, Jan 12 at 3:00PM
  return formatDate(appointmentDateInLocalTime, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    dayPeriod: 'short',
    weekday: 'short',
  })
}

export const shouldHighlightProviderAppt = ({
  shouldDisplayFastTrackBooking = false,
  appointmentDate,
  appointmentTime,
  timeZone,
  isBCProvider = false,
  shouldShowAvailabilityPreference = false,
  userAvailabilityPreference = null,
}: {
  shouldDisplayFastTrackBooking?: boolean
  appointmentDate: string
  appointmentTime: string
  timeZone: string
  isBCProvider?: boolean
  shouldShowAvailabilityPreference?: boolean | undefined
  userAvailabilityPreference?: string[] | null
}) => {
  // Highlight fast track booking when user does NOT have a valid availability preference, i.e. when
  // they have specified valid date/times
  const hasValidAvailabilityPreference =
    shouldShowAvailabilityPreference &&
    userAvailabilityPreference &&
    userAvailabilityPreference.length > 0 &&
    !userAvailabilityPreference.includes('flexible')
  return (
    !hasValidAvailabilityPreference &&
    shouldDisplayFastTrackBooking &&
    is24HoursInTheFuture({
      startDate: appointmentDate,
      startTime: appointmentTime,
      timeZone,
    }) &&
    isBCProvider
  )
}

export const shouldDisplayFastTrackBooking = ({
  provider,
  isTopRecommendedProvider = false,
}: {
  provider: ProviderInfo
  isTopRecommendedProvider?: boolean
}) => {
  return isProviderBlendedCareType({ provider }) || isTopRecommendedProvider
}

export const getProviderVideoLinks = ({
  providers,
  justIds = false,
}: {
  providers?: ProviderInfo[]
  justIds?: boolean
}) => {
  if (!providers?.length) {
    return []
  }

  const mappedProviders = providers.map((provider) => {
    const videoLink = provider.profileVideo?.videoLink

    if (!videoLink) {
      return
    }
    if (justIds) {
      const id = videoLink.split('/').pop()
      return id
    } else {
      return videoLink
    }
  })
  return compact(mappedProviders)
}

export const doesProviderHaveVideo = ({ provider }: { provider: ProviderInfo }) => {
  return getProviderVideoLinks({ providers: [provider] }).length > 0
}

/**
 * Calendar service returns availability slots in the provider's timezone, so we'll need to convert
 * the time from provider to the client's timezone
 */
export const getProviderAvailableSlots = ({ provider, timeZone }: { provider: ProviderInfo; timeZone: string }) => {
  if (!provider?.calendar) return []

  const calendar = provider.calendar as ProviderCalendar
  const providerTimezone = calendar.timeZoneId
  const datetimes: string[] = []

  calendar.availability?.forEach((datetime) => {
    const date = datetime.date
    const times = datetime.times
    return times.forEach((time) => {
      const utcDate = zonedTimeToUtc(new Date(`${date} ${time}`), providerTimezone)
      const dateInUserTimeZone = formatInTimeZone(utcDate, timeZone, "yyyy-MM-dd'T'HH:mm:ssXXX")
      return datetimes.push(dateInUserTimeZone)
    })
  })

  return datetimes
}

export const makeProviderDataForTracking = ({
  providers,
  timeZone,
}: {
  providers: ProviderInfo[]
  timeZone: string
}) => {
  const providerData = providers.map((provider) => {
    return {
      providerId: provider.lyra_id,
      video: doesProviderHaveVideo({ provider }),
      [S4C_PROPERTIES.AI_BIO]: doesProviderHaveAIBioSummary({ provider }),
      [S4C_PROPERTIES.PROVIDER_AVAILABLE_SLOTS]: getProviderAvailableSlots({ provider, timeZone }),
    }
  })

  return {
    providers: providerData,
  }
}

export const makeFavoritedProviderDataForTracking = ({
  providers,
  transformedProviderAvailabilities,
  timeZone,
  favoritedProvidersMetadataMap,
}: {
  providers: ProviderInfo[]
  timeZone: string
  transformedProviderAvailabilities: { [providerId: string]: any }
  favoritedProvidersMetadataMap: Record<string, FavoritedProvidersMetadata> | null
}) => {
  const providerData = providers.map((provider) => {
    const providerAvailabilities = get(transformedProviderAvailabilities, `${provider.lyra_id}.availability`, [])
    const provider_clickable_slots = providerAvailabilities.flatMap(
      ({ date, times }: { date: string; times: string[] }) => {
        return times.map((time) => {
          return `${date}T${time}`
        })
      },
    )

    return {
      providerId: provider.lyra_id,
      video: doesProviderHaveVideo({ provider }),
      provider_availability: isProviderAvailableForBooking({ provider }),
      remaining_days: getRemainingDaysUntil7DayExpiration({
        createDateUnixTimestamp: favoritedProvidersMetadataMap?.[provider.lyra_id]?.createDateUnixTimestamp,
      }),
      provider_clickable_slots,
      timeZone,
    }
  })

  return providerData
}

/**
 * Returns a list of provider conditions from the ExplainClinicalMatch. Used in Picked for you v1, v2, v3
 *
 * @param {ProviderExplainClinicalMatch[]} explainClinicalMatchList Array of ExplainClinicalMatch
 * @param {boolean} useMatchReason useMatchReason = false (Default) -> Return only the unique match values from ExplainClinicalMatch - these are provider's conditions that relate to the primary needs and triage terms. useMatchReason = true -> Return the unique match reasons from ExplainClinicalMatch - primary needs and triage terms that directly relate to a provider's specified conditions
 */
export const getExplainClinicalMatchConditions = ({
  explainClinicalMatchList,
  useMatchReason = false,
}: {
  explainClinicalMatchList: ProviderExplainClinicalMatch[]
  useMatchReason?: boolean
}) => {
  if (explainClinicalMatchList?.length > 0) {
    if (useMatchReason) {
      const clinicalReasonList = explainClinicalMatchList.map((clinicalReason) => {
        // if there's a mapping for the translation, use that - otherwise we'll try the startCase
        return (
          PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG[clinicalReason?.reason?.componentName] ||
          startCaseDelimitedString(clinicalReason?.reason?.componentName, ' ', ' ')
        )
      })
      return Array.from(new Set(clinicalReasonList))
    } else {
      // Can be deleted once moved over to Picked for you v3
      // if it's a mixed case string, use the mapping for it, otherwise we'll try the startCase
      return Array.from(
        new Set(
          explainClinicalMatchList.map(
            (clinicalMatch) =>
              PROVIDER_CONDITIONS_MAPPING_TO_TRANSLATION_MSG[clinicalMatch?.match?.value] ||
              clinicalMatch?.match?.value.replace(/\w+/g, startCase),
          ),
        ),
      )
    }
  }
  return []
}

export const getProviderFirstNameToDisplay = ({ provider }: { provider?: ProviderInfo }) =>
  provider?.display_first_name || provider?.first_name || ''

export const getDistanceToProvider = ({
  distance,
  useKilometers,
}: {
  distance: number
  useKilometers: boolean
}): DistanceToProviderValue => {
  return {
    value: Math.round(distance * 10) / 10,
    unit: useKilometers ? DistanceUnitAbbreviation.KM : DistanceUnitAbbreviation.MI,
  }
}

export const getProviderAIBioSummary = ({ provider }: { provider?: ProviderInfo }) =>
  provider?.bio_summary_surfaceable && provider?.bio_summary ? provider?.bio_summary : null

export const doesProviderHaveAIBioSummary = ({ provider }: { provider?: ProviderInfo }) =>
  Boolean(getProviderAIBioSummary({ provider }))

// Check if any providers have an AI bio summary
export const doProvidersHaveAiBio = ({ providers }: { providers: ProviderInfo[] }) => {
  return providers.filter((provider) => provider?.bio_summary_surfaceable && provider?.bio_summary)?.length > 0
}

export const getProviderSpecializations = ({
  provider,
  showPickedForYouV3 = false,
}: {
  provider?: ProviderInfo
  showPickedForYouV3?: boolean
}) =>
  showPickedForYouV3 && provider?.explainClinicalMatch && provider?.explainClinicalMatch?.length > 0
    ? getExplainClinicalMatchConditions({
        explainClinicalMatchList: provider.explainClinicalMatch,
        useMatchReason: showPickedForYouV3,
      })
    : provider?.conditions

export const getProviderTypeDisplayFromTaxonomy = ({
  providerProgramTaxonomy,
  isUserInternational = false,
}: {
  providerProgramTaxonomy?: ProviderProgramTaxonomy[] | ProviderProgramTaxonomy
  isUserInternational?: boolean
}) => {
  let providerTypeDisplay
  const programTaxonomy = isArray(providerProgramTaxonomy) ? providerProgramTaxonomy : [providerProgramTaxonomy]
  if (programTaxonomy?.find((taxonomy) => taxonomy?.treatment === PROGRAM_TAXONOMY.treatment.therapy)) {
    if (isUserInternational) {
      providerTypeDisplay = ProviderTypesPluralEnum.COUNSELLORS
    } else {
      providerTypeDisplay = ProviderTypesPluralEnum.THERAPISTS
    }
  } else if (programTaxonomy?.find((taxonomy) => taxonomy?.treatment === PROGRAM_TAXONOMY.treatment.coaching)) {
    providerTypeDisplay = ProviderTypesPluralEnum.COACHES
  } else if (
    programTaxonomy?.find((taxonomy) => taxonomy?.treatment === PROGRAM_TAXONOMY.treatment.medicationManagement)
  ) {
    providerTypeDisplay = ProviderTypesPluralEnum.PRESCRIBERS
  } else {
    providerTypeDisplay = ProviderTypesPluralEnum.PROVIDERS
  }
  return providerTypeDisplay
}

export const getProviderIdsShownForQuickLinks = ({
  providerProfileLyraId,
  providerIds,
}: {
  providerProfileLyraId: string
  providerIds: string[]
}) => {
  const indexOfShownProvider = providerIds.indexOf(providerProfileLyraId)
  // Look to the previous slot of the position of the shown provider
  // If it exists, use that position for the provider recommendation to the left.
  // If it does not, that means the shown provider is the top provider and to use the position after
  // this provider as the provider recommendation to the left
  const prevProviderId = providerIds[indexOfShownProvider - 1] ?? providerIds[indexOfShownProvider + 1]

  const indexOfFirstProvider = providerIds.indexOf(prevProviderId)
  let nextProviderId = null
  // If there are only two providers in provider results, we will not show a second provider
  if (providerIds.length > 2) {
    if (indexOfShownProvider === providerIds.length - 1) {
      nextProviderId = providerIds[0]
    } else if (indexOfFirstProvider + 1 === indexOfShownProvider) {
      nextProviderId = providerIds[indexOfShownProvider + 1]
    } else {
      nextProviderId = providerIds[indexOfFirstProvider + 1]
    }
  }
  return { prevProviderId, nextProviderId }
}

export const getShouldShowUrgencyToBookv2 = ({
  selectedTreatmentOption,
  programTaxonomy,
  providerCapacity,
}: {
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null
  programTaxonomy?: ProviderProgramTaxonomy[]
  providerCapacity?: { [treatmentOption in string]: number }
}) => {
  const givenTreatmentOption = selectedTreatmentOption ?? getInferredLCXTreatmentOption({ programTaxonomy })
  if (!givenTreatmentOption) {
    return false
  }

  return shouldShowPopularProviderText({
    programTaxonomy,
    providerCapacity,
    selectedTreatmentOption: givenTreatmentOption,
  })
}

export const getTreatmentOptionsFromProviderCapacity = ({
  providerCapacity,
  isAUDSupported,
  isGSCSupported,
}: {
  providerCapacity: ProviderCapacity
  isAUDSupported: boolean
  isGSCSupported: boolean
}) => {
  const programNamesInProviderCapacity = Object.keys(providerCapacity) as LTProgramName[]
  return programNamesInProviderCapacity
    .map((tn) => ProgramNamesInProviderCapacityToTreatmentNames[tn])
    .filter((x) => {
      if (x === TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER && !isAUDSupported) {
        return false
      }
      if (x === TREATMENT_OPTIONS.GUIDED_SELF_CARE && !isGSCSupported) {
        return false
      }
      return x != undefined
    }) as TREATMENT_OPTIONS_TYPE[]
}

/*
   The values in Capacity (libs/shared-app-logic/src/models/user/User.ts) loosely
   map to TREATMENT_OPTION_TYPE (libs/shared-app-logic/src/features/common/constants/treatmentOptions.ts).
   This function derives the Provider's offered TREATMENT_OPTION_TYPE from its ProviderCapacity
   object.
*/
export const makeTreatmentRecsFromProviderCapacityForDirectBooking = ({
  providerCapacity,
  isAUDSupported,
  isGSCSupported,
}: {
  providerCapacity: ProviderCapacity
  isAUDSupported: boolean
  isGSCSupported: boolean
}) => {
  const eligibleTreatmentOptions = getTreatmentOptionsFromProviderCapacity({
    providerCapacity,
    isAUDSupported,
    isGSCSupported,
  })
  if (eligibleTreatmentOptions.length > 2) {
    throw new Error('More than 2 treatment options found in providerCapacity')
  }

  const possiblePrimaryRecs = [
    TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
    TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER,
    TREATMENT_OPTIONS.COACHING,
    TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
  ]
  const possibleSecondaryRecs = [TREATMENT_OPTIONS.GUIDED_SELF_CARE]

  let primaryTreatmentOption
  let alternateTreatmentOption
  const secondaryTreatmentOptions = []
  for (const t of possiblePrimaryRecs) {
    if (!eligibleTreatmentOptions.includes(t)) {
      continue
    }

    if (primaryTreatmentOption == undefined) {
      primaryTreatmentOption = t
    } else if (alternateTreatmentOption == undefined) {
      alternateTreatmentOption = t
    }
  }

  for (const t of possibleSecondaryRecs) {
    if (!eligibleTreatmentOptions.includes(t) || t === primaryTreatmentOption || t === alternateTreatmentOption) {
      continue
    }

    secondaryTreatmentOptions.push(t)
  }

  if (primaryTreatmentOption != undefined && alternateTreatmentOption != undefined) {
    const primaryTreatmentCapacity =
      providerCapacity[TreatmentNamesToProgramNamesInProviderCapacity[primaryTreatmentOption] ?? '']
    const alternateTreatmentCapacity =
      providerCapacity[TreatmentNamesToProgramNamesInProviderCapacity[alternateTreatmentOption] ?? '']
    if (primaryTreatmentCapacity === 0 && alternateTreatmentCapacity > 0) {
      ;[primaryTreatmentOption, alternateTreatmentOption] = [alternateTreatmentOption, primaryTreatmentOption]
    }
  }

  return {
    primaryTreatmentOption,
    alternateTreatmentOption,
    secondaryTreatmentOptions,
  }
}

export const getTreatmentRecsFromProviderCapacityForDirectBookingInList = ({
  providerCapacity,
  isAUDSupported,
  isGSCSupported,
}: {
  providerCapacity: ProviderCapacity
  isAUDSupported: boolean
  isGSCSupported: boolean
}) => {
  const eligibleTreatmentOptions = makeTreatmentRecsFromProviderCapacityForDirectBooking({
    providerCapacity,
    isAUDSupported,
    isGSCSupported,
  })
  let allOptions = [
    eligibleTreatmentOptions.primaryTreatmentOption,
    eligibleTreatmentOptions.alternateTreatmentOption,
    ...eligibleTreatmentOptions.secondaryTreatmentOptions,
  ].filter(Boolean)
  allOptions = [...new Set(allOptions)]
  return allOptions
}

export const getProviderProgramTaxonomy = (provider: ProviderInfo, programs: Program[]): ProviderProgramTaxonomy[] => {
  if ('program_taxonomy' in provider) {
    return provider.program_taxonomy ?? []
  }
  // Fallbacks when provider.program_taxonomy is not available (should not happen for most providers)

  // Map legacy lyra_type -> provider type
  const providerStringType = provider?.lyra_type?.toUpperCase?.()
  const enumValue = ProviderEnums[providerStringType as keyof typeof ProviderEnums] ?? ProviderEnums.THERAPIST
  const providerType: ProviderType = PROVIDER_TYPES[enumValue]

  // Return all CTPOs from LT program, if available
  const matchingProgram = providerType.program ? programs.find((p) => p.name === providerType.program) : undefined
  if (matchingProgram?.program_taxonomy) {
    return matchingProgram.program_taxonomy
  }

  // Fallback for non-LT programs (e.g. DIRECT_ACCESS_THERAPY, DIRECT_ACCESS_MEDS)
  const fallback = providerType.fallbackProgramTaxonomy
  return fallback ? [...fallback] : []
}

export const doesProviderSupportProgramTaxonomy = (
  provider: ProviderInfo,
  programTaxonomy: ProviderProgramTaxonomy,
  programs: Program[],
) => {
  const providerTaxonomyOptions = getProviderProgramTaxonomy(provider, programs)
  return Boolean(
    providerTaxonomyOptions.find(
      (pt) =>
        pt.clientele === programTaxonomy.clientele &&
        pt.treatment === programTaxonomy.treatment &&
        pt.partner === programTaxonomy.partner &&
        pt.offering === programTaxonomy.offering,
    ),
  )
}

export const getMemberRecommendedBadgeData = ({
  showMemberRecommendedBadgeFlag,
  isProviderRecommended = false,
  isTopProviderInResultsList = false,
  isScreenEligibleToDisplayBadge = false,
}: {
  showMemberRecommendedBadgeFlag?: boolean
  isProviderRecommended?: boolean
  isTopProviderInResultsList?: boolean
  isScreenEligibleToDisplayBadge?: boolean
}) => {
  if (showMemberRecommendedBadgeFlag == null) return

  const isEligibleForMemberRecommendedBadge = isProviderRecommended
  if (
    showMemberRecommendedBadgeFlag &&
    isProviderRecommended &&
    (isTopProviderInResultsList || isScreenEligibleToDisplayBadge)
  ) {
    return {
      shouldShowMemberRecommendedBadge: true,
      isEligibleForMemberRecommendedBadge: true,
    }
  }
  return {
    shouldShowMemberRecommendedBadge: false,
    isEligibleForMemberRecommendedBadge,
  }
}

/**
 * Helper function to extract modality and location metadata for a specific availability date
 * from the Calendar Service API response.
 *
 * When `selectedOnsiteLocationKey` is provided, the matching slot is run through
 * `filterTimeSlotByOnsiteLocation` so that BOTH slots at a non-matching location are downgraded
 * to VIDEO (location stripped) and IN_PERSON slots at a non-matching location are dropped. This
 * keeps the fast-track booking payload consistent with the calendar booking page, where the same
 * downgrade is already applied per slot before render.
 *
 * @param providerAvailability - Array of provider availability data from Calendar Service API
 * @param targetDate - The date to find modality and location information for
 * @param selectedOnsiteLocationKey - Optional onsite location key for the DBL onsite flow
 * @returns An object containing modalityType and appointmentLocation for the target date, or empty object if not found
 */
export const getModalityAndLocationForDate = (
  providerAvailability: ProviderAvailabilities[] | undefined,
  targetDate: Date | undefined,
  selectedOnsiteLocationKey?: string | null,
): { modalityType?: ModalityType; appointmentLocation?: InPersonLocation } => {
  if (!providerAvailability || !targetDate) {
    return {}
  }

  const availabilitySlots = providerAvailability[0]?.availability

  if (!availabilitySlots?.length) {
    return {}
  }

  // Find the slot that matches the target date
  // Compare timestamps instead of ISO strings because the formats may differ
  // (e.g., '2025-11-21T00:00:00.000Z' vs '2025-11-21T08:00:00-08:00')
  const targetTimestamp = targetDate.getTime()
  const matchingSlot = availabilitySlots.find((slot) => {
    if (!slot?.start) return false
    const slotTimestamp = new Date(slot.start).getTime()
    return slotTimestamp === targetTimestamp
  })

  if (!matchingSlot) {
    return {}
  }

  if (selectedOnsiteLocationKey) {
    // filterTimeSlotByOnsiteLocation returns either [] (slot dropped) or a single-element array
    // (slot kept, possibly with a downgraded modality), so the [0] read is intentional.
    const filtered = filterTimeSlotByOnsiteLocation({
      timeSlot: {
        modality_type: matchingSlot.modality_type as ModalityType,
        location: matchingSlot.location as InPersonLocation,
      },
      selectedOnsiteLocationKey,
    })[0]
    if (!filtered) return {}
    return {
      modalityType: filtered.modality_type,
      appointmentLocation: filtered.location,
    }
  }

  return {
    modalityType: matchingSlot.modality_type as ModalityType,
    appointmentLocation: matchingSlot.location as InPersonLocation,
  }
}

export const getProviderKeywordType = ({
  showProviderAdjectives,
  providerAdjectives,
  providerBioKeywords,
}: {
  showProviderAdjectives?: boolean
  providerAdjectives?: string[]
  providerBioKeywords?: string[]
}) => {
  if (showProviderAdjectives && providerAdjectives && providerAdjectives.length > 0) {
    return ProviderKeywordType.ADJECTIVES
  } else if (providerBioKeywords && providerBioKeywords.length > 0) {
    return ProviderKeywordType.BIO_KEYWORDS
  }
  return null
}

/**
 * Returns provider keywords based on configuration and availability.
 *
 * When `showProviderAdjectives` is true and `providerAdjectives` are available,
 * returns the adjectives. Otherwise, returns bio keywords. The returned keywords
 * are shuffled using a stable algorithm based on the provider ID.
 *
 * @param providerBioKeywords - Keywords extracted from provider's bio
 * @param providerAdjectives - Descriptive adjectives for the provider
 * @param showProviderAdjectives - Whether to prioritize adjectives over bio keywords
 * @param providerId - Unique identifier for the provider (used for stable shuffling)
 * @returns Shuffled array of keywords, or null if no keywords available
 */
export const getProviderKeywords = ({
  providerBioKeywords,
  providerAdjectives,
  showProviderAdjectives,
  providerId,
}: {
  providerBioKeywords?: string[]
  providerAdjectives?: string[]
  showProviderAdjectives?: boolean
  providerId?: string
}) => {
  if (!providerId) {
    return null
  }

  let keywords: string[] | undefined | null

  const providerKeywordType = getProviderKeywordType({
    showProviderAdjectives,
    providerAdjectives,
    providerBioKeywords,
  })

  if (providerKeywordType === ProviderKeywordType.ADJECTIVES) {
    keywords = providerAdjectives
  } else if (providerKeywordType === ProviderKeywordType.BIO_KEYWORDS) {
    keywords = providerBioKeywords
  }

  return keywords?.length ? stableShuffleArrayWithUUID(keywords, providerId) : null
}

/**
 * Derives the correct program taxonomy from an appointment object.
 *
 * This function prioritizes:
 *
 * 1. The appointment's existing `programTaxonomy` if it exists and is valid
 * 2. The `fallbackTaxonomy` (from `useProgramTaxonomy`) if it is a valid option within the program
 *    derived from `appointment.programId` — it already considers provider context (e.g. BlendedCare
 *    vs DirectAccess partner for LCT providers). ICE-6671.
 * 3. `taxonomyOptions[0]` for the program when the fallback is not a valid option
 * 4. Falling back to the provided fallback taxonomy or DEFAULT_PROGRAM_TAXONOMY
 *
 * @param appointment - The appointment object to derive the taxonomy from
 * @param fallbackTaxonomy - The fallback taxonomy (typically from useProgramTaxonomy hook)
 * @returns The correct ProviderProgramTaxonomy for the appointment
 */
export const getProgramTaxonomyFromAppointment = (
  appointment: Appointment | null | undefined,
  fallbackTaxonomy?: ProviderProgramTaxonomy | null,
  programs?: Program[],
): ProviderProgramTaxonomy => {
  if (appointment?.programTaxonomy && isKnownTreatmentCTPO(appointment.programTaxonomy)) {
    return appointment.programTaxonomy
  }

  const taxonomyOptions = programs?.find((p) => p.id === appointment?.programId)?.program_taxonomy
  if (taxonomyOptions?.length) {
    const matchesFallback = (pt: ProviderProgramTaxonomy) =>
      pt.clientele === fallbackTaxonomy?.clientele &&
      pt.treatment === fallbackTaxonomy?.treatment &&
      pt.partner === fallbackTaxonomy?.partner &&
      pt.offering === fallbackTaxonomy?.offering

    // TODO: taxonomyOptions[0] depends on how LT sorts the program taxonomy options
    return fallbackTaxonomy && taxonomyOptions.some(matchesFallback) ? fallbackTaxonomy : taxonomyOptions[0]
  }

  return fallbackTaxonomy ?? DEFAULT_PROGRAM_TAXONOMY
}

export const makeProviderKeywordTrackingData = ({
  provider,
  showProviderAdjectives,
  positionOnPage,
  includeSharedData = true,
}: {
  provider: ProviderInfo
  showProviderAdjectives?: boolean
  positionOnPage?: number
  includeSharedData?: boolean
}) => {
  const providerBioKeywords = provider.provider_descriptors
  const providerAdjectives = provider.testimonialData?.providerAdjectives
  const providerKeywords = getProviderKeywords({
    providerBioKeywords,
    providerAdjectives,
    showProviderAdjectives,
    providerId: provider.lyra_id,
  })

  const sharedData = includeSharedData ? { providerID: provider.lyra_id, position_on_page: positionOnPage } : {}

  if (providerKeywords?.length) {
    const providerKeywordType = getProviderKeywordType({
      showProviderAdjectives,
      providerAdjectives,
      providerBioKeywords,
    })
    return {
      ...sharedData,
      testimonial_adjectives: providerKeywordType === ProviderKeywordType.ADJECTIVES ? providerKeywords : [],
      bio_keywords: providerKeywordType === ProviderKeywordType.BIO_KEYWORDS ? providerKeywords : [],
    }
  } else {
    return {
      ...sharedData,
      testimonial_adjectives: [],
      bio_keywords: [],
    }
  }
}

/**
 * Generates tracking data for provider keywords across a list of providers.
 *
 * This function processes a list of providers and creates tracking data objects
 * containing keyword information (either testimonial adjectives or bio keywords)
 * along with provider ID and position metadata. The keywords are determined by
 * the `showProviderAdjectives` flag and availability of data.
 *
 * @param providers - Array of provider information objects to process
 * @param showProviderAdjectives - Whether to prioritize testimonial adjectives over bio keywords
 * @returns Array of tracking data objects, each containing:
 *   - providerID: The provider's unique identifier
 *   - position_on_page: 1-indexed position in the list
 *   - testimonial_adjectives: Array of adjective keywords (if applicable)
 *   - bio_keywords: Array of bio descriptor keywords (if applicable)
 *
 * @example
 * const trackingData = makeProvidersListKeywordTrackingData({
 *   providers: [provider1, provider2],
 *   showProviderAdjectives: true
 * })
 * // Returns: [
 * //   { providerID: 'abc', position_on_page: 1, testimonial_adjectives: [...], bio_keywords: [] },
 * //   { providerID: 'def', position_on_page: 2, testimonial_adjectives: [...], bio_keywords: [] }
 * // ]
 */
export const makeProvidersListKeywordTrackingData = ({
  providers,
  showProviderAdjectives,
}: {
  providers: ProviderInfo[]
  showProviderAdjectives?: boolean
}) => {
  return providers.map((provider, index) => {
    return makeProviderKeywordTrackingData({ provider, showProviderAdjectives, positionOnPage: index + 1 })
  })
}
